<template>
  <!-- ── INITIAL LOAD GATE ────────────────────────────────────────────────────
       While we are fetching the consultation for the first time, render NOTHING
       but a centred spinner. This prevents the full page skeleton from flashing
       before a possible 404 redirect (the Axios interceptor in api.js handles the
       redirect; we just need to keep the DOM clear until it fires).
  ────────────────────────────────────────────────────────────────────────── -->
  <div
    v-if="consultationLoading"
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-50 dark:bg-slate-900"
    aria-label="Loading"
    role="status"
  >
    <div class="flex flex-col items-center gap-3">
      <svg class="w-10 h-10 animate-spin text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
    </div>
  </div>

  <div
    v-else
    class="relative flex h-screen max-h-screen w-full max-w-full overflow-hidden bg-slate-50 dark:bg-slate-900"
    :dir="dir"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />

    <!-- Main content container (screen-constrained, no window scroll) -->
    <div
      class="flex flex-col flex-1 min-w-0 w-full h-full max-h-screen overflow-hidden"
    >
      <!-- Top bar -->
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        class="shrink-0"
        @toggle-sidebar="toggleSidebar"
      />

      <!-- Main Chat Viewport -->
      <main
        class="flex-1 min-h-0 flex flex-col p-2.5 sm:p-4 md:p-5 overflow-hidden"
      >
        <div class="max-w-6xl mx-auto w-full flex-1 min-h-0 flex flex-col">
          <!-- Page Header Strip (Compact, informative, shrink-0) -->
          <div
            class="flex items-center justify-between gap-3 mb-2.5 sm:mb-3 shrink-0"
          >
            <!-- Left: Title, number, subtitle -->
            <div class="flex items-center gap-2.5 min-w-0">
              <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition shadow-xs shrink-0"
                @click="router.back()"
                :title="t('back') || (lang === 'ar' ? 'رجوع' : 'Back')"
              >
                <i
                  class="mdi text-lg"
                  :class="lang === 'ar' ? 'mdi-arrow-right' : 'mdi-arrow-left'"
                ></i>
              </button>

              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <h1
                    class="text-base sm:text-lg font-bold text-slate-900 dark:text-white truncate"
                  >
                    {{ t("chat_title") || (lang === 'ar' ? "المحادثة" : "Chat") }}
                  </h1>

                  <!-- Consultation Number Badge with Copy -->
                  <div
                    v-if="consultationNumber"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-mono font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700/80 cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                    @click="copyConsultationNumber"
                    :title="copiedNumber ? (lang === 'ar' ? 'تم النسخ!' : 'Copied!') : (lang === 'ar' ? 'انقر لنسخ رقم الاستشارة' : 'Click to copy number')"
                  >
                    <span>#{{ consultationNumber }}</span>
                    <i
                      class="mdi text-[12px]"
                      :class="copiedNumber ? 'mdi-check text-emerald-600' : 'mdi-content-copy opacity-60'"
                    ></i>
                  </div>
                </div>

                <p
                  v-if="consultationSubject"
                  class="text-xs text-slate-500 dark:text-slate-400 truncate max-w-md hidden sm:block"
                >
                  {{ consultationSubject }}
                </p>
              </div>
            </div>

            <!-- Right: Status Badges & Quick Action -->
            <div class="flex items-center gap-2 shrink-0">
              <!-- Loading Skeleton for Status Badges -->
              <template v-if="consultationLoading">
                <span class="h-6 w-20 sm:w-24 rounded-full bg-slate-200/80 dark:bg-slate-800 animate-pulse"></span>
              </template>

              <template v-else>
                <!-- Consultation Status Pill -->
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border transition-all"
                  :class="statusBadgeClasses"
                >
                  <!-- Active pulsing beacon for Active Paid consultation -->
                  <span
                    v-if="isActive"
                    class="relative flex h-2 w-2"
                  >
                    <span
                      class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                    ></span>
                    <span
                      class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"
                    ></span>
                  </span>
                  <i
                    v-else
                    class="mdi text-[14px]"
                    :class="statusIconClass"
                  ></i>
                  <span>{{ statusText }}</span>
                </span>

                <!-- Payment Status Pill (if not finished) -->
                <span
                  v-if="!isFinished && paymentStatus"
                  class="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border"
                  :class="isPaid
                    ? 'bg-teal-50 text-teal-700 dark:bg-teal-950/30 dark:text-teal-300 border-teal-200 dark:border-teal-800'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300 border-amber-200 dark:border-amber-800'"
                >
                  <i
                    class="mdi text-[13px]"
                    :class="isPaid ? 'mdi-credit-card-check-outline' : 'mdi-clock-alert-outline'"
                  ></i>
                  <span>{{ isPaid ? (lang === 'ar' ? 'مدفوعة' : 'Paid') : (lang === 'ar' ? 'غير مدفوعة' : 'Unpaid') }}</span>
                </span>
              </template>

              <!-- Details Link -->
              <router-link
                :to="{ name: 'consultations.show', params: { id } }"
                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750 transition shadow-2xs"
                :title="lang === 'ar' ? 'تفاصيل الاستشارة' : 'Consultation details'"
              >
                <i class="mdi mdi-open-in-new text-[13px]"></i>
                <span class="hidden md:inline">{{ lang === 'ar' ? 'تفاصيل الاستشارة' : 'Details' }}</span>
              </router-link>
            </div>
          </div>

          <!-- Main Card Wrapper (Takes 100% remaining screen height) -->
          <section
            class="flex-1 min-h-0 flex flex-col overflow-hidden bg-white dark:bg-slate-900 border rounded-2xl shadow-sm transition-all duration-300"
            :class="cardBorderClasses"
          >
            <!-- Card Header: Participant info + live indicators -->
            <header
              class="shrink-0 flex items-center justify-between px-4 sm:px-5 py-2.5 sm:py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-800/80 backdrop-blur-xs"
            >
              <!-- Skeleton during initial check -->
              <div v-if="consultationLoading" class="flex items-center gap-2.5 min-w-0">
                <div class="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-200 dark:bg-slate-750 animate-pulse shrink-0"></div>
                <div class="space-y-1.5">
                  <div class="h-3.5 w-32 rounded bg-slate-200 dark:bg-slate-750 animate-pulse"></div>
                  <div class="h-2.5 w-20 rounded bg-slate-100 dark:bg-slate-800 animate-pulse"></div>
                </div>
              </div>

              <!-- Participant Info when loaded -->
              <div v-else class="flex items-center gap-2.5 min-w-0">
                <div
                  class="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full text-xs font-bold shrink-0 transition-colors"
                  :class="headerAvatarClass"
                >
                  <i
                    v-if="isFinished"
                    class="mdi mdi-lock-check-outline text-base"
                  ></i>
                  <i
                    v-else-if="!isPaid"
                    class="mdi mdi-lock text-base text-amber-600 dark:text-amber-400"
                  ></i>
                  <span v-else>{{ otherParticipantInitial }}</span>
                </div>

                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <span
                      class="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate"
                    >
                      {{ otherParticipantName || t("conversation") || (lang === 'ar' ? "المحادثة" : "Conversation") }}
                    </span>
                    <span
                      v-if="otherParticipantRole"
                      class="text-[10px] px-1.5 py-0.2 rounded font-medium bg-slate-200/70 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                    >
                      {{ otherParticipantRole }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                    <!-- Dynamic state text -->
                    <span
                      v-if="isActive"
                      class="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium"
                    >
                      <span class="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      {{ lang === 'ar' ? 'محادثة نشطة ومباشرة' : 'Active conversation' }}
                    </span>
                    <span
                      v-else-if="isFinished"
                      class="inline-flex items-center gap-1 text-slate-500 font-medium"
                    >
                      <i class="mdi mdi-archive-outline text-xs"></i>
                      {{ lang === 'ar' ? 'المحادثة مغلقة ومؤرشفة' : 'Closed & archived' }}
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium"
                    >
                      <i class="mdi mdi-lock text-xs"></i>
                      {{ lang === 'ar' ? 'بانتظار السداد لتفعيل الدردشة' : 'Pending payment' }}
                    </span>

                    <!-- Appointment Time if available -->
                    <span
                      v-if="appointmentScheduleText"
                      class="hidden md:inline-flex items-center gap-1 text-slate-400 border-s border-slate-200 dark:border-slate-700 ps-2"
                    >
                      <i class="mdi mdi-calendar-clock text-xs"></i>
                      <span>{{ appointmentScheduleText }}</span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Header Action Buttons -->
              <div class="flex items-center gap-2 shrink-0">
                <!-- Session URL Link (if applicable) -->
                <a
                  v-if="isPaid && appointmentMethod === 'session' && appointmentUrl"
                  :href="appointmentUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition shadow-2xs"
                >
                  <i class="mdi mdi-video-outline text-sm"></i>
                  <span>{{ lang === 'ar' ? 'رابط الجلسة' : 'Session link' }}</span>
                </a>

                <!-- Manual refresh button -->
                <button
                  type="button"
                  class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                  :disabled="isLoading"
                  @click="refreshMessages"
                  :title="lang === 'ar' ? 'تحديث المحادثة' : 'Refresh chat'"
                >
                  <i
                    class="mdi mdi-refresh text-base"
                    :class="{ 'animate-spin text-primary': isLoading }"
                  ></i>
                </button>
              </div>
            </header>

            <!-- Messages Area (Scrollable within card height) -->
            <div class="relative flex-1 min-h-0 flex flex-col overflow-hidden bg-slate-50/30 dark:bg-slate-900/30">
              <div
                ref="messagesScrollRef"
                class="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-3 sm:px-6 py-4 space-y-3.5"
                @scroll="handleScroll"
              >
                <!-- Initial Loading Skeleton (Shimmering chat messages) -->
                <div
                  v-if="consultationLoading || (isLoading && !messagesList.length)"
                  class="h-full flex flex-col justify-end space-y-4 py-4 px-2"
                >
                  <div class="flex items-end gap-2 justify-start max-w-[75%]">
                    <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse shrink-0"></div>
                    <div class="space-y-2 p-3 rounded-2xl rounded-tl-xs bg-slate-100 dark:bg-slate-800 w-48 sm:w-60 animate-pulse">
                      <div class="h-2.5 w-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      <div class="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded"></div>
                    </div>
                  </div>

                  <div class="flex items-end gap-2 justify-end max-w-[75%] ms-auto">
                    <div class="space-y-2 p-3 rounded-2xl rounded-tr-xs bg-slate-200/80 dark:bg-slate-700/80 w-56 sm:w-72 animate-pulse">
                      <div class="h-2.5 w-14 bg-slate-300 dark:bg-slate-600 rounded ms-auto"></div>
                      <div class="h-3 w-full bg-slate-300 dark:bg-slate-600 rounded"></div>
                      <div class="h-3 w-2/3 bg-slate-300 dark:bg-slate-600 rounded ms-auto"></div>
                    </div>
                    <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse shrink-0"></div>
                  </div>

                  <div class="flex items-end gap-2 justify-start max-w-[75%]">
                    <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse shrink-0"></div>
                    <div class="space-y-2 p-3 rounded-2xl rounded-tl-xs bg-slate-100 dark:bg-slate-800 w-40 sm:w-52 animate-pulse">
                      <div class="h-2.5 w-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      <div class="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded"></div>
                    </div>
                  </div>
                </div>

                <!-- STATE: UNPAID Banner -->
                <div
                  v-else-if="!isPaid"
                  class="h-full flex flex-col items-center justify-center py-10 px-4 text-center my-auto"
                >
                  <div class="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center mb-3 text-2xl shadow-xs">
                    <i class="mdi mdi-lock-clock"></i>
                  </div>
                  <h3 class="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-200">
                    {{ lang === 'ar' ? 'المحادثة مغلقة بانتظار سداد الاستشارة' : 'Chat Locked — Payment Required' }}
                  </h3>
                </div>

                <!-- STATE: NO MESSAGES YET -->
                <div
                  v-else-if="!messagesList.length"
                  class="h-full flex flex-col items-center justify-center py-10 px-4 text-center my-auto"
                >
                  <div
                    class="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 text-2xl shadow-xs"
                    :class="isFinished
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                      : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 ring-4 ring-emerald-500/10'"
                  >
                    <i
                      class="mdi"
                      :class="isFinished ? 'mdi-flag-checkered' : 'mdi-chat-processing-outline'"
                    ></i>
                  </div>

                  <h3 class="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-200 mb-1">
                    {{
                      isFinished
                        ? (lang === 'ar' ? 'الاستشارة منتهية بدون رسائل' : 'Consultation Finished')
                        : (lang === 'ar' ? 'المحادثة مفعلة ومتاحة الآن!' : 'Chat is Active & Ready!')
                    }}
                  </h3>

                  <p class="text-xs text-slate-500 dark:text-slate-400 max-w-sm leading-relaxed">
                    {{
                      isFinished
                        ? (lang === 'ar' ? 'تم إنهاء الاستشارة بنجاح.' : 'This consultation has ended.')
                        : (lang === 'ar'
                            ? 'تم سداد الاستشارة وتفعيل المحادثة. يمكنك الآن كتابة استفسارك والتواصل فوراً.'
                            : 'Payment confirmed. You can now start communicating directly.')
                    }}
                  </p>
                </div>

                <!-- STATE: MESSAGES LIST -->
                <template v-else>
                  <div
                    v-for="m in messagesList"
                    :key="m.id"
                    class="flex items-end gap-2 group"
                    :class="m.left ? 'justify-start' : 'justify-end'"
                  >
                    <!-- LEFT Avatar -->
                    <div
                      v-if="m.left"
                      class="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 text-[11px] font-bold rounded-full shrink-0 shadow-2xs select-none"
                      :style="{
                        backgroundColor: m.identity.bg,
                        color: m.identity.fg,
                      }"
                    >
                      {{ m.identity.initial }}
                    </div>

                    <!-- Chat Bubble -->
                    <div
                      class="relative max-w-[85%] sm:max-w-[75%] px-3.5 py-2.5 text-xs sm:text-sm leading-relaxed shadow-xs transition-shadow"
                      :class="bubbleClasses(m)"
                    >
                      <!-- Sender Name Badge -->
                      <div
                        class="inline-flex items-center mb-1 px-2 py-[1px] rounded-full text-[10px] font-semibold select-none"
                        :style="badgeStyle(m)"
                      >
                        {{ m.sender_name || t("other") || (lang === 'ar' ? "مستخدم" : "User") }}
                      </div>

                      <!-- Message Text -->
                      <div class="break-words whitespace-pre-wrap select-text">
                        {{ m.text }}
                      </div>

                      <!-- Attachment (if provided) -->
                      <div
                        v-if="m.attachment"
                        class="mt-2 pt-1.5 border-t border-white/20 dark:border-slate-700/60"
                      >
                        <a
                          :href="m.attachment"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="inline-flex items-center gap-1 text-[11px] font-semibold underline underline-offset-2 opacity-90 hover:opacity-100"
                        >
                          <i class="mdi mdi-paperclip"></i>
                          <span>{{ lang === 'ar' ? 'عرض المرفق' : 'View attachment' }}</span>
                        </a>
                      </div>

                      <!-- Timestamp -->
                      <div
                        class="mt-1 text-[10px] opacity-75 text-right font-sans"
                        :class="m.left ? 'text-slate-500 dark:text-slate-400' : 'text-white/80'"
                      >
                        {{ m.time }}
                      </div>
                    </div>

                    <!-- RIGHT Avatar -->
                    <div
                      v-if="!m.left"
                      class="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 text-[11px] font-bold rounded-full shrink-0 shadow-2xs select-none"
                      :style="{
                        backgroundColor: m.identity.bg,
                        color: m.identity.fg,
                      }"
                    >
                      {{ m.identity.initial }}
                    </div>
                  </div>
                </template>
              </div>

              <!-- Floating Scroll to Bottom Button -->
              <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2"
              >
                <button
                  v-if="!isAtBottom && messagesList.length"
                  type="button"
                  class="absolute bottom-3 end-5 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 shadow-md hover:bg-slate-50 dark:hover:bg-slate-700 transition"
                  @click="scrollToBottom"
                  :title="lang === 'ar' ? 'الانتقال إلى آخر رسالة' : 'Scroll to bottom'"
                >
                  <i class="mdi mdi-chevron-down text-lg"></i>
                </button>
              </transition>
            </div>

            <!-- Error Banner -->
            <div
              v-if="error"
              class="shrink-0 px-4 py-1.5 text-xs text-rose-600 bg-rose-50 dark:bg-rose-950/40 border-t border-rose-200 dark:border-rose-900/60 flex items-center justify-between"
            >
              <span>{{ error }}</span>
              <button
                type="button"
                class="text-rose-500 hover:text-rose-700 text-sm font-bold px-1"
                @click="error = ''"
              >
                ✕
              </button>
            </div>

            <!-- Card Footer / Chat Box: Dynamic states -->

            <!-- 0. LOADING SKELETON STATE (while checking consultation status) -->
            <footer
              v-if="consultationLoading"
              class="shrink-0 px-4 sm:px-6 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
            >
              <div class="flex items-center gap-3">
                <div class="h-10 flex-1 rounded-2xl bg-slate-200/80 dark:bg-slate-800 animate-pulse"></div>
                <div class="w-10 h-10 rounded-2xl bg-slate-200/80 dark:bg-slate-800 animate-pulse shrink-0"></div>
              </div>
            </footer>

            <!-- 1. ACTIVE STATE (isPaid && !isFinished) -->
            <footer
              v-else-if="isActive"
              class="shrink-0 px-3 sm:px-5 py-2.5 sm:py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/70 backdrop-blur-xs"
            >
              <div class="flex items-end gap-2 sm:gap-3">
                <!-- Composer Input Box -->
                <div
                  class="flex items-end flex-1 gap-2 px-3 py-1.5 sm:py-2 border shadow-2xs rounded-2xl bg-white dark:bg-slate-800/95 border-slate-200 dark:border-slate-700 focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary transition-all"
                >
                  <span
                    class="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-xl text-slate-400 shrink-0"
                    aria-hidden="true"
                  >
                    <i class="mdi mdi-message-outline text-[18px]"></i>
                  </span>

                  <textarea
                    ref="textareaRef"
                    v-model="draft"
                    rows="1"
                    class="w-full py-1 sm:py-1.5 text-xs sm:text-sm leading-5 bg-transparent resize-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none max-h-28 custom-scrollbar"
                    :placeholder="lang === 'ar' ? 'اكتب رسالتك هنا... (Enter للإرسال)' : 'Type a message... (Enter to send)'"
                    @keydown.enter.exact.prevent="sendMessage"
                  ></textarea>
                </div>

                <!-- Send Action Button -->
                <button
                  type="button"
                  class="inline-flex items-center justify-center text-white shadow-sm w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-primary hover:bg-primary-light active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0"
                  :disabled="sending || !draft.trim()"
                  @click="sendMessage"
                  :aria-label="t('send') || (lang === 'ar' ? 'إرسال' : 'Send')"
                  :title="t('send') || (lang === 'ar' ? 'إرسال' : 'Send')"
                >
                  <i
                    v-if="sending"
                    class="mdi mdi-loading mdi-spin text-[18px]"
                  ></i>
                  <i
                    v-else
                    class="mdi mdi-send text-[17px] sm:text-[18px]"
                    :class="lang === 'ar' ? 'rotate-180' : ''"
                  ></i>
                </button>
              </div>

              <!-- Keyboard shortcut hint -->
              <div class="mt-1.5 text-[10px] text-slate-400 dark:text-slate-500 px-1 hidden sm:block">
                {{
                  lang === 'ar'
                    ? 'اضغط Enter للإرسال • Shift + Enter لسطر جديد'
                    : 'Press Enter to send • Shift + Enter for a new line'
                }}
              </div>
            </footer>

            <!-- 2. FINISHED STATE (isFinished) -->
            <footer
              v-else-if="isFinished"
              class="shrink-0 px-4 sm:px-6 py-3 sm:py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-100/80 dark:bg-slate-800/80"
            >
              <div class="flex items-center justify-between flex-wrap gap-2.5">
                <div class="flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center shrink-0">
                    <i class="mdi mdi-lock-check-outline text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                      {{ lang === 'ar' ? 'تم إنهاء هذه الاستشارة وإغلاق المحادثة' : 'Consultation Finished — Chat Closed' }}
                    </h4>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400">
                      {{ lang === 'ar' ? 'تم حفظ سجل المحادثة للرجوع إليه والمراجعة فقط.' : 'Conversation history is archived for reference.' }}
                    </p>
                  </div>
                </div>

                <router-link
                  :to="{ name: 'consultations.show', params: { id } }"
                  class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition shadow-2xs"
                >
                  <i class="mdi mdi-file-document-outline text-xs"></i>
                  <span>{{ lang === 'ar' ? 'عرض تفاصيل الاستشارة' : 'View Details' }}</span>
                </router-link>
              </div>
            </footer>

            <!-- 3. UNPAID STATE (!isPaid && !isFinished) -->
            <footer
              v-else
              class="shrink-0 px-4 sm:px-6 py-3 border-t border-amber-200/70 dark:border-amber-900/40 bg-amber-50/70 dark:bg-amber-950/20"
            >
              <div class="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-200">
                <i class="mdi mdi-lock text-base text-amber-600 dark:text-amber-400 shrink-0"></i>
                <span>
                  {{
                    lang === 'ar'
                      ? 'صندوق المحادثة مغلق حتى يتم تأكيد سداد قيمة الاستشارة.'
                      : 'Message box is locked until consultation payment is completed.'
                  }}
                </span>
              </div>
            </footer>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(5, 139, 128, 0.45) transparent;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(5, 139, 128, 0.03);
  border-radius: 999px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(5, 139, 128, 0.4);
  border-radius: 999px;
  border: 1.5px solid transparent;
  background-clip: padding-box;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(5, 139, 128, 0.85);
  box-shadow: 0 0 8px rgba(5, 139, 128, 0.4);
}
:deep(html.dark) .custom-scrollbar {
  scrollbar-color: rgba(94, 234, 212, 0.45) transparent;
}
:deep(html.dark) .custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.4);
}
:deep(html.dark) .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(94, 234, 212, 0.4);
  border: 1.5px solid transparent;
  background-clip: padding-box;
}
:deep(html.dark) .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(94, 234, 212, 0.9);
  box-shadow: 0 0 10px rgba(94, 234, 212, 0.5);
}
</style>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { usePageI18n } from "@/composables/usePageI18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import api from "@/services/api";

const router = useRouter();
const route = useRoute();
const id = route.params.id;

const { lang, t, dir } = usePageI18n();

watch(lang, () => {
  messagesList.value = messagesList.value.map((m) => {
    const left = isLeftSide(Boolean(m.isOwner));
    return { ...m, left };
  });
});

/* sidebar */
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");
function toggleSidebar() {
  if (window.innerWidth < 768)
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  else isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

/* state */
const consultation = ref(null);
const consultationLoading = ref(true);
const paymentStatusLoading = computed(() => consultationLoading.value);
const isLoading = ref(false);
const sending = ref(false);
const error = ref("");
const messagesList = ref([]);
const draft = ref("");
const textareaRef = ref(null);
const copiedNumber = ref(false);
let copiedTimer = null;

let pollTimer = null;
let pollInFlight = false;

function stopChatPoll() {
  if (pollTimer) {
    window.clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function scheduleChatPoll() {
  stopChatPoll();
  if (!isActive.value || document.visibilityState !== "visible") return;
  pollTimer = window.setTimeout(runChatPoll, 5000);
}

async function runChatPoll() {
  if (!isActive.value || document.visibilityState !== "visible") return;
  if (pollInFlight) {
    scheduleChatPoll();
    return;
  }
  pollInFlight = true;
  try {
    await fetchChat({ silent: true });
  } finally {
    pollInFlight = false;
    scheduleChatPoll();
  }
}

function onChatVisibilityChange() {
  if (document.visibilityState === "hidden") {
    stopChatPoll();
    return;
  }
  if (isActive.value) {
    runChatPoll();
  }
}

const lastMessageId = ref(null);
const messagesScrollRef = ref(null);
const isAtBottom = ref(true);

/* Computed Consultation Attributes */
const consultationNumber = computed(() => {
  return consultation.value?.consultation_number || String(id || "");
});

const consultationSubject = computed(() => {
  return consultation.value?.subject || "";
});

const statusId = computed(() => {
  return Number(
    consultation.value?.status?.id ??
    consultation.value?.status_id ??
    0
  );
});

const isFinished = computed(() => {
  if (consultationLoading.value) return false;
  if (statusId.value === 5) return true;
  const rawEn = String(
    consultation.value?.status?.title?.en ||
    consultation.value?.status?.name ||
    consultation.value?.status?.key ||
    consultation.value?.status ||
    ""
  ).toLowerCase().trim();
  const rawAr = String(
    consultation.value?.status?.title?.ar ||
    ""
  ).trim();
  return (
    rawEn === "finished" ||
    rawEn === "closed" ||
    rawEn === "completed" ||
    rawEn === "answered" ||
    rawAr === "منتهية" ||
    rawAr === "مكتملة" ||
    rawAr === "تمت الإجابة"
  );
});

const paymentStatus = computed(() => {
  return (
    consultation.value?.payment_status ||
    consultation.value?.invoice?.status ||
    null
  );
});

const isPaid = computed(() => {
  if (consultationLoading.value) return false;
  const pStatus = String(paymentStatus.value || "").toLowerCase().trim();
  return pStatus === "paid";
});

const isActive = computed(() => {
  if (consultationLoading.value) return false;
  return isPaid.value && !isFinished.value;
});

const appointmentMethod = computed(() => {
  return String(
    consultation.value?.oppointment_method ||
    consultation.value?.appointment_method ||
    ""
  ).toLowerCase();
});

const appointmentUrl = computed(() => {
  return String(
    consultation.value?.oppointment_url ||
    consultation.value?.appointment_url ||
    ""
  );
});

const statusText = computed(() => {
  if (consultationLoading.value) return "...";
  if (isFinished.value) {
    return lang.value === "ar" ? "منتهية" : "Finished";
  }
  if (statusId.value === 4) {
    return lang.value === "ar" ? "تم القبول" : "Accepted";
  }
  return (
    consultation.value?.status?.title?.[lang.value] ||
    consultation.value?.status?.title?.ar ||
    consultation.value?.status?.name ||
    (lang.value === "ar" ? "قيد المتابعة" : "In Progress")
  );
});

const statusBadgeClasses = computed(() => {
  if (consultationLoading.value) {
    return "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 border-slate-200 dark:border-slate-700";
  }
  if (isFinished.value) {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700";
  }
  if (isActive.value) {
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60 shadow-2xs";
  }
  return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800/60";
});

const statusIconClass = computed(() => {
  if (isFinished.value) return "mdi-lock-check-outline";
  if (isActive.value) return "mdi-check-circle-outline";
  return "mdi-clock-outline";
});

const cardBorderClasses = computed(() => {
  if (consultationLoading.value) {
    return "border-slate-200 dark:border-slate-800";
  }
  if (isFinished.value) {
    return "border-slate-200 dark:border-slate-800";
  }
  if (isActive.value) {
    return "border-emerald-500/40 dark:border-emerald-500/30 ring-1 ring-emerald-500/10 shadow-md shadow-emerald-500/5";
  }
  return "border-amber-200/70 dark:border-amber-900/40";
});

/* Participant Info */
const consultantName = computed(() => consultation.value?.handled_by?.name || "");
const clientName = computed(() => consultation.value?.created_by?.name || "");

const isCurrentUserConsultant = computed(() => {
  if (!owner.value.name) return false;
  return owner.value.name === consultantName.value;
});

const otherParticipantName = computed(() => {
  if (isCurrentUserConsultant.value) {
    return clientName.value || (lang.value === "ar" ? "العميل" : "Client");
  }
  return consultantName.value || (lang.value === "ar" ? "المستشار" : "Consultant");
});

const otherParticipantRole = computed(() => {
  if (isCurrentUserConsultant.value) {
    return lang.value === "ar" ? "العميل" : "Client";
  }
  return lang.value === "ar" ? "المستشار" : "Consultant";
});

const otherParticipantInitial = computed(() => {
  const name = otherParticipantName.value;
  return name ? name.trim().charAt(0).toUpperCase() : "C";
});

const headerAvatarClass = computed(() => {
  if (consultationLoading.value) {
    return "bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500";
  }
  if (isFinished.value) return "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300";
  if (!isPaid.value) return "bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300";
  return "bg-primary/10 text-primary dark:bg-primary/20 dark:text-emerald-400";
});

const appointmentScheduleText = computed(() => {
  const d = consultation.value?.oppointment_date || consultation.value?.date || "";
  const t = consultation.value?.oppointment_time || "";
  if (!d && !t) return "";
  return [d, t].filter(Boolean).join(" • ");
});

function copyConsultationNumber() {
  if (!consultationNumber.value) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(consultationNumber.value);
  }
  copiedNumber.value = true;
  if (copiedTimer) clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    copiedNumber.value = false;
  }, 2000);
}

/* Auth User / Owner Cookie detection */
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

const owner = ref({ id: null, name: "" });
function loadOwnerFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) return;

  try {
    const u = JSON.parse(raw) || {};
    owner.value.id = u?.id ?? null;
    owner.value.name = String(u?.name || u?.full_name || "").trim();
  } catch {}
}

function isOwnerMessage(rawMsg) {
  if (rawMsg?.is_owner != null) return Boolean(rawMsg.is_owner);
  if (rawMsg?.is_me != null) return Boolean(rawMsg.is_me);

  if (owner.value.id != null && rawMsg?.sender_id != null) {
    return String(rawMsg.sender_id) === String(owner.value.id);
  }

  const senderName = String(rawMsg?.sender || rawMsg?.sender_name || "").trim();
  if (owner.value.name && senderName) return senderName === owner.value.name;

  return false;
}

/**
 * Message Direction:
 * EN: owner = LEFT, others = RIGHT
 * AR: owner = RIGHT, others = LEFT
 */
function isLeftSide(isOwner) {
  const ownerOnLeft = lang.value !== "ar";
  return ownerOnLeft ? isOwner : !isOwner;
}

function hashString(str) {
  let h = 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h);
}

function colorFromKey(key) {
  const h = hashString(key) % 360;
  return {
    bg: `hsl(${h} 70% 45%)`,
    badgeBg: `hsl(${h} 70% 92%)`,
    badgeFg: `hsl(${h} 70% 30%)`,
    fg: "#fff",
  };
}

function buildIdentity(rawMsg) {
  const senderName =
    String(rawMsg?.sender || rawMsg?.sender_name || "").trim() || "U";
  const senderId = rawMsg?.sender_id != null ? String(rawMsg.sender_id) : "";
  const key = senderId ? `id:${senderId}` : `name:${senderName.toLowerCase()}`;

  const colors = colorFromKey(key);
  const initial = senderName.trim().charAt(0).toUpperCase() || "U";

  return {
    key,
    initial,
    ...colors,
  };
}

function bubbleClasses(m) {
  const isMe = !m.left;
  if (isMe) {
    return lang.value === "ar"
      ? "bg-gradient-to-r from-primary to-[#067269] text-white rounded-2xl rounded-tr-xs"
      : "bg-gradient-to-r from-primary to-[#067269] text-white rounded-2xl rounded-tl-xs";
  }
  return lang.value === "ar"
    ? "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200/90 dark:border-slate-700/80 rounded-2xl rounded-tl-xs"
    : "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200/90 dark:border-slate-700/80 rounded-2xl rounded-tr-xs";
}

function badgeStyle(m) {
  if (!m.left) {
    return {
      backgroundColor: "rgba(255, 255, 255, 0.18)",
      color: "rgba(255, 255, 255, 0.95)",
    };
  }
  return {
    backgroundColor: m.identity?.badgeBg || "#f1f5f9",
    color: m.identity?.badgeFg || "#334155",
  };
}

/* API Calls */
async function fetchConsultation() {
  consultationLoading.value = true;
  try {
    const res = await api.get(`/consultations/${id}`);
    const c = res.data?.data || res.data || {};
    consultation.value = c;
  } catch {
    // 404 is handled globally by the Axios interceptor in api.js.
    // Other errors simply leave consultation null (chat remains locked/empty).
    consultation.value = null;
  } finally {
    consultationLoading.value = false;
  }
}

function updateAtBottom() {
  const el = messagesScrollRef.value;
  if (!el) return;
  const threshold = 16;
  isAtBottom.value =
    el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
}

function handleScroll() {
  updateAtBottom();
}

async function scrollToBottom() {
  await nextTick();
  const el = messagesScrollRef.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
}

async function refreshMessages() {
  if (isLoading.value) return;
  await fetchChat({ silent: false });
}

/* fetch chat messages */
async function fetchChat({ silent = false } = {}) {
  if (!isPaid.value) {
    if (!silent) isLoading.value = false;
    return;
  }
  if (!silent) isLoading.value = true;
  if (!silent) error.value = "";
  try {
    const res = await api.get(`/consultations/${id}/chat`);

    const payload = res.data?.data || {};
    const rows = payload.chat || [];

    const mapped = rows.map((m) => {
      const isOwner = isOwnerMessage(m);
      const left = isLeftSide(isOwner);

      return {
        id: m.id,
        text: m.message || "",
        sender_name: m.sender || m.sender_name || "",
        sender_id: m.sender_id ?? null,
        isOwner,
        left,
        time: m.date || "",
        identity: buildIdentity(m),
        attachment: m.attachment || m.attachments || null,
      };
    });

    if (!messagesList.value.length) {
      messagesList.value = mapped;
      lastMessageId.value = mapped.length ? mapped[mapped.length - 1].id : null;
      if (isAtBottom.value) await scrollToBottom();
    } else {
      const lastId = lastMessageId.value;
      let newItems = mapped;
      if (lastId != null) {
        const lastIndex = mapped.findIndex(
          (m) => String(m.id) === String(lastId),
        );
        newItems = lastIndex >= 0 ? mapped.slice(lastIndex + 1) : mapped;
      }

      if (newItems.length) {
        messagesList.value = [...messagesList.value, ...newItems];
        lastMessageId.value = newItems[newItems.length - 1].id;
        if (isAtBottom.value) await scrollToBottom();
      }
    }
  } catch (e) {
    if (!silent) {
      error.value = e?.response?.data?.message || t("chat_load_failed") || (lang.value === 'ar' ? 'فشل تحميل المحادثة' : 'Failed to load conversation');
    }
  } finally {
    if (!silent) isLoading.value = false;
  }
}

/* send message */
async function sendMessage() {
  if (!isActive.value) return;
  const text = String(draft.value || "").trim();
  if (!text || sending.value) return;

  sending.value = true;
  error.value = "";
  try {
    await api.post(`/consultations/${id}/chat`, { message: text });

    draft.value = "";
    await fetchChat({ silent: true });
    await scrollToBottom();
  } catch (e) {
    error.value = e?.response?.data?.message || t("chat_send_failed") || (lang.value === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message');
  } finally {
    sending.value = false;
  }
}

onMounted(() => {
  loadOwnerFromCookie();
  document.addEventListener("visibilitychange", onChatVisibilityChange);
  fetchConsultation().then(() => {
    if (isPaid.value) {
      fetchChat();
      if (isActive.value) {
        scheduleChatPoll();
      }
      nextTick(updateAtBottom);
    }
  });
});

onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", onChatVisibilityChange);
  stopChatPoll();
  if (copiedTimer) clearTimeout(copiedTimer);
});
</script>
